import { Controller } from "@hotwired/stimulus"

/**
 * Bundle Modal Controller
 * 
 * Handles the open/close behavior of the bundle purchase modal.
 * Manages body scroll prevention, backdrop clicks, and keyboard navigation.
 * 
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5
 */
export default class extends Controller {
  static targets = ["modal", "backdrop", "content"]
  static values = { isOpen: Boolean, autoOpen: Boolean }

  connect() {
    // Bind methods for event listeners
    this.handleEscape = this.handleEscape.bind(this)
    this.handleBackdropClick = this.handleBackdropClick.bind(this)
    
    // Add keyboard listener for Escape key
    document.addEventListener("keydown", this.handleEscape)

    if (this.autoOpenValue) {
      this.open()
      this.clearAutoOpenQueryParams()
    }
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener("keydown", this.handleEscape)
    
    // Restore body scroll if modal was open when disconnected
    if (this.isOpenValue) {
      this.restoreBodyScroll()
    }
  }

  /**
   * Opens the modal and prevents body scroll
   * Validates: Requirements 1.1, 1.2
   */
  open() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      this.modalTarget.classList.add("flex")
      this.preventBodyScroll()
      this.isOpenValue = true
      
      // Focus first focusable element for accessibility
      this.focusFirstInput()
    }
  }

  /**
   * Closes the modal and restores body scroll
   * Validates: Requirements 1.3
   */
  close() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.classList.remove("flex")
      this.restoreBodyScroll()
      this.isOpenValue = false
    }
  }

  /**
   * Handles clicks on the backdrop to close modal
   * Only closes if the click target is the backdrop itself, not modal content
   * Validates: Requirements 1.4
   */
  handleBackdropClick(event) {
    // Only close if clicking directly on the backdrop, not on modal content
    if (this.hasBackdropTarget && event.target === this.backdropTarget) {
      this.close()
    }
  }

  /**
   * Handles Escape key press to close modal
   * Validates: Requirements 1.5
   */
  handleEscape(event) {
    if (event.key === "Escape" && this.isOpenValue) {
      this.close()
    }
  }

  /**
   * Prevents scrolling on the body when modal is open
   * Validates: Requirements 1.2
   */
  preventBodyScroll() {
    document.body.style.overflow = "hidden"
    document.body.style.paddingRight = this.getScrollbarWidth() + "px"
  }

  /**
   * Restores body scrolling when modal is closed
   * Validates: Requirements 1.3
   */
  restoreBodyScroll() {
    document.body.style.overflow = ""
    document.body.style.paddingRight = ""
  }

  /**
   * Calculates scrollbar width to prevent layout shift
   */
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth
  }

  /**
   * Focuses the first input element in the modal for accessibility
   */
  focusFirstInput() {
    setTimeout(() => {
      const firstInput = this.modalTarget.querySelector("input, select, textarea, button")
      if (firstInput) {
        firstInput.focus()
      }
    }, 100)
  }

  clearAutoOpenQueryParams() {
    const url = new URL(window.location.href)
    let changed = false

    ;["open_bundle_modal", "bundle_modal"].forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param)
        changed = true
      }
    })

    if (changed) {
      const nextUrl = `${url.pathname}${url.search}${url.hash}`
      window.history.replaceState({}, "", nextUrl)
    }
  }
}
